import { FC, ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Button, PillGroup } from '@base-app';
import { DateRangeOptionId, DateRangeSelection } from '@builder';
import { LAYOUT_NUMBER, PillId } from './LayoutOne.types';
import { getFirstDayOfQuarter, getLastDayOfQuarter } from '@/common/utils';
import { STUDY_INSIGHTS_TRANSLATION } from '@/modules/study-insights/constants';
import { PillIds } from '../../shared/types/PillIds';
import { ScrollDirection } from '@/common/types/ScrollDirection.type';
import { StyledInfoPanel, StyledRightArrowIcon, StyledSubPageContainer, StyledSubPageHeader } from './LayoutOne.styles';
import { Variants } from '@/common/types';
import { Pill } from '@/common/components/base-app/organisms/PillGroup/PillGroup.types';
import ChartsPanel from '../../shared/ChartsPanel/ChartsPanel';
import DateFilter from '../../shared/DateFilter/DateFilter';
import InfoPanel from '../../shared/InfoPanel/InfoPanel';
import SectionTitle from '../../shared/SectionTitle/SectionTitle';
import useConsentedParticipantsStore from '@/modules/my-account/store/useConsentedParticipantsStore/useConsentedParticipantsStore';
import useQueryPartnerConsentedParticipantData from '@/modules/study-insights/hooks/useQueryPartnerConsentedParticipantData';
import useScrollDirection from '@/common/hooks/useScrollDirection/useScrollDirection';
import ParticipantLayout from '../../shared/ParticipantLayout/ParticipantLayout';
import FeatureFlags from '@/modules/feature-flags/types/feature-flags.types';
import { Permissions } from '@modules/permissions/types';
import { usePermissions } from '@modules/permissions/hooks';
import { useFeatureFlags } from '@/modules/feature-flags/hooks';
import { formatNumber } from '@/modules/study-insights/utils';
import { GroupKey } from '@/modules/study-insights/types/api';
import { useQueryEditGoals } from '@/modules/study-insights/hooks';

const LayoutOne: FC = () => {
  const { t } = useTranslation(STUDY_INSIGHTS_TRANSLATION);
  const { direction } = useScrollDirection();
  const [isNavSticky, setIsNavSticky] = useState<boolean>(false);
  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeSelection>();
  const [startDate, setStartDate] = useState<Date | undefined>(getFirstDayOfQuarter());
  const [endDate, setEndDate] = useState<Date | undefined>(getLastDayOfQuarter());
  const [totalConsentedAllTime, setTotalConsentedAllTime] = useState<number>(0);
  const [totalConversionRateAllTime, setTotalConversionRateAllTime] = useState<number>(0);
  const [goalLine, setGoalLine] = useState<number>(0);
  const [tooltipConsented, setTooltipConsented] = useState<string>(
    t('participant_recruitment.chart_tooltip', {
      section: PillIds.CONSENTED.charAt(0).toUpperCase() + PillIds.CONSENTED.slice(1),
    }),
  );
  const [currentPill, setCurrentPill] = useState<string>('');
  const [totalConsentedNumberSideContent, setTotalConsentedNumberSideContent] = useState<string>('');
  const [totalConsentedPercentageSideContent, setTotalConsentedPercentageSideContent] = useState<number>(0);
  const [totalConsentedRemainingSideContent, setTotalConsentedRemainingSideContent] = useState<number>(0);
  const [selectedPill, setSelectedPill] = useState<PillId>(PillId.CONSENTED_PARTICIPANTS);

  // TODO: Remove campaign tracking from layout 1
  const { isFeatureEnabled } = useFeatureFlags();
  const { hasPermission } = usePermissions();
  const hasCampaignTrackingPartnerLevel = hasPermission(Permissions.CAMPAIGN_TRACKING);
  const isCampaingTrackingEnabled = isFeatureEnabled(FeatureFlags.CAMPAIGN_TRACKING) && hasCampaignTrackingPartnerLevel;

  const handleOnClickPill = (id: string): void => {
    setSelectedPill(id as PillId);
  };

  const PILL_LIST: Pill[] = [
    {
      label: t('consented_participants.title'),
      id: PillId.CONSENTED_PARTICIPANTS,
    },
    {
      label: t('campaign_tracking.title'),
      id: PillId.CAMPAIGN_TRACKING,
    },
  ];

  const {
    endDate: storeEndDate,
    startDate: storeStartDate,
    setEndDate: setStoreEndDate,
    setStartDate: setStoreStartDate,
  } = useConsentedParticipantsStore();

  const consentedPillOptions = [
    {
      id: PillIds.CONSENTED,
      label: t('participant_recruitment.consented.title'),
    },
    {
      id: PillIds.RATE_CONSENTED,
      label: t('participant_recruitment.conversion_rate'),
    },
  ];

  const handlePillClick = (id: string): void => {
    setCurrentPill(id);
  };

  const handleSelectDateRange = (selection: DateRangeSelection): void => {
    setStartDate(selection.startDate);
    setEndDate(selection.endDate);
    setSelectedDateRange(selection);
  };

  const handleScroll = (): void => {
    const container = document.getElementById('subpage-layout-one');
    const containerPosition = container?.getBoundingClientRect().top ?? 0;
    const isSecondNavSticky = containerPosition < 0;

    setIsNavSticky(isSecondNavSticky);
  };

  const renderSubPageHeader = (pillId: PillIds): ReactNode => {
    return (
      <StyledSubPageHeader
        isSticky={isNavSticky}
        css={{
          top: direction === ScrollDirection.DOWN ? 0 : '$stickyNav$height',
          marginBottom: '$6',
        }}
      >
        <StyledSubPageContainer>
          <DateFilter
            rangeTextStyles={{ fontSize: '24px' }}
            css={{ display: 'flex', flexDirection: 'row-reverse', gap: '$4' }}
            onDateRangeChange={handleSelectDateRange}
            selectedDateRange={selectedDateRange}
            storeStartDate={storeStartDate}
            storeEndDate={storeEndDate}
            layout={LAYOUT_NUMBER}
          />
          <Button
            css={{ display: 'none', padding: '$1 $4' }}
            onClick={() => handlePillClick(pillId)}
            variant={Variants.SECONDARY}
          >
            {t('participant_recruitment.expand_graph_view')}
            <Box css={{ display: 'inline-flex' }}>
              <StyledRightArrowIcon />
            </Box>
          </Button>
        </StyledSubPageContainer>
      </StyledSubPageHeader>
    );
  };

  const renderConsentedInfoPanel = (): ReactNode => {
    return (
      <StyledInfoPanel>
        <InfoPanel
          containerStyles={{ p: '$0' }}
          infoTitle={t('participant_recruitment.total_consented').toLocaleUpperCase()}
          numberText={totalConsentedNumberSideContent}
          previousPeriod={{
            percentage: totalConsentedPercentageSideContent,
            period: selectedDateRange?.selectedDateRange,
          }}
          remaining={totalConsentedRemainingSideContent}
        />
      </StyledInfoPanel>
    );
  };

  const getConsentTooltip = (): void => {
    setTooltipConsented(
      currentPill === PillIds.CONSENTED
        ? t('participant_recruitment.chart_tooltip', {
            section: currentPill.charAt(0).toUpperCase() + currentPill.slice(1),
          })
        : t('participant_recruitment.expanded_chart_consented_conversion_tooltip'),
    );
  };

  const groupKey = GroupKey.BY_MONTH; // currently only option
  const { data: consentedParticipantData, isLoading: isLoadingPartnerConsentedData } =
    useQueryPartnerConsentedParticipantData({
      endDate,
      startDate,
      groupKey,
    });

  useEffect(() => {
    const consentedDataKey = 'consented';

    if (selectedDateRange?.startDate && selectedDateRange?.endDate && consentedParticipantData?.overView?.goalLine) {
      setGoalLine(consentedParticipantData?.overView?.goalLine ?? 0);
      setTotalConsentedAllTime(consentedParticipantData?.overView.totalConsented ?? 0);
      setTotalConversionRateAllTime(consentedParticipantData?.overView.totalConversionRate ?? 0);

      if (consentedParticipantData?.groupData?.stats?.statsData) {
        for (const entry of consentedParticipantData?.groupData?.stats?.statsData) {
          if (entry.label === consentedDataKey) {
            setTotalConsentedNumberSideContent(String(entry.value) ?? '0');
            setTotalConsentedPercentageSideContent(entry.percentChange);
            setTotalConsentedRemainingSideContent(consentedParticipantData?.overView.consentedGoal ?? 0);
          }
        }
      }
    }
  }, [consentedParticipantData, selectedDateRange]);

  useEffect(() => {
    setSelectedDateRange({
      endDate,
      startDate,
      selectedDateRange: DateRangeOptionId.THIS_QUARTER,
    });

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const { data: editGoalsData } = useQueryEditGoals();

  useEffect(() => {
    if (!editGoalsData) {
      return;
    }
    setStoreStartDate(editGoalsData.startDate);
    setStoreEndDate(editGoalsData.endDate);
  }, [editGoalsData]);

  useEffect(() => {
    switch (currentPill) {
      case PillIds.CONSENTED:
      case PillIds.RATE_CONSENTED:
        getConsentTooltip();
        break;
      default:
        break;
    }
  }, [currentPill]);

  return (
    <>
      <Box
        css={{
          margin: 'auto',
          width: '100%',
          maxWidth: '$studyInsights$containerMaxWidth',
          paddingLeft: '$8',
          paddingBottom: '$6',
        }}
      >
        {isCampaingTrackingEnabled && (
          <PillGroup items={PILL_LIST} selectedPill={selectedPill} onClickPill={handleOnClickPill} />
        )}
      </Box>

      {selectedPill === PillId.CONSENTED_PARTICIPANTS && (
        <ParticipantLayout
          id="consented-layout-one"
          isLoading={isLoadingPartnerConsentedData}
          sectionTitle={
            <SectionTitle
              css={{ paddingLeft: '$6' }}
              label={t('participant_recruitment.consented_participants')}
              sub={t('participant_recruitment.recruitment_data')}
            />
          }
          labelLeftTop={t('participant_recruitment.total_consented')}
          labelRightTop={t('participant_recruitment.total_conversion_rate')}
          labelRightBottom={t('participant_recruitment.samples_received_consented')}
          numberLeft={formatNumber(totalConsentedAllTime)}
          numberRight={totalConversionRateAllTime.toString()}
          sideContent={renderConsentedInfoPanel()}
          subHeader={renderSubPageHeader(PillIds.CONSENTED)}
        >
          <ChartsPanel
            chartData={consentedParticipantData}
            chartTooltip={tooltipConsented}
            data-testid="consented-charts-panel"
            handleSelectedPill={handlePillClick}
            pillOptions={consentedPillOptions}
            selectedDateRange={selectedDateRange}
            selectedPill={PillIds.CONSENTED}
            goalLine={goalLine}
          />
        </ParticipantLayout>
      )}
    </>
  );
};

export default LayoutOne;
